import { useId, useRef, useState } from "react";
import { problemOf, OFFLINE } from "../data/problems";
import { newSyncKey } from "../data/progressStore";
import { saveLearnerObservation } from "../data/teacherWorkApi";
import { announceWorkspaceChange } from "../data/usePolledResource";
import { formatWhen } from "./WorkState";
import ui from "./ui.module.css";

export const MAX_OBSERVATION = 4000;

const FAILURE_COPY = {
  offline: "You're offline. Your note is still here; save it again when you're connected.",
  signIn: "Your sign-in has ended. Sign in again, then save your note.",
  refused: "This note can't be saved for this learner any more.",
  invalid: "This note couldn't be saved. Check it isn't too long and try again.",
  failed: "Your note wasn't saved. Please try again.",
};

const failureOf = (error) => {
  if (problemOf(error) === OFFLINE) return "offline";
  const status = error?.response?.status;
  if (status === 401) return "signIn";
  if (status === 403 || status === 404) return "refused";
  if (status === 400) return "invalid";
  return "failed";
};

// "Count the beads · Finished 20 Oct, 09:00": the time tells apart two tries
// at the same activity.
const workOption = (item) =>
  [item.title, item.when].filter(Boolean).join(" · ");

const AddObservation = ({ classroomId, learnerId, work, onSaved, onCancel }) => {
  const textId = useId();
  const aboutId = useId();
  const [text, setText] = useState("");
  // The note is about no particular work until the teacher picks one.
  const [aboutAttempt, setAboutAttempt] = useState("");
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState(null);
  // One operation per note: a retry after a lost answer re-sends the same id,
  // so the gateway returns the note it already saved instead of a second one.
  const operationId = useRef(newSyncKey());

  const save = async (event) => {
    event.preventDefault();
    if (!text.trim() || saving) return;
    setSaving(true);
    setFailure(null);
    const about = work.find((item) => item.attemptId === aboutAttempt);
    try {
      const saved = await saveLearnerObservation(classroomId, learnerId, {
        operationId: operationId.current,
        observation: text.trim(),
        ...(about
          ? { lessonDeliveryId: about.deliveryId, attemptId: about.attemptId }
          : {}),
      });
      onSaved(saved);
    } catch (error) {
      setFailure(failureOf(error));
      setSaving(false);
    }
  };

  return (
    <form className={ui.reviewForm} onSubmit={save}>
      <label htmlFor={textId}>Observation</label>
      <textarea
        id={textId}
        value={text}
        onChange={(event) => setText(event.target.value)}
        maxLength={MAX_OBSERVATION}
        placeholder="What did you notice?"
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus
      />
      {work.length > 0 && (
        <>
          <label htmlFor={aboutId}>Which work is it about?</label>
          <select
            id={aboutId}
            className={ui.aboutPicker}
            value={aboutAttempt}
            onChange={(event) => setAboutAttempt(event.target.value)}
          >
            <option value="">No specific work</option>
            {work.map((item) => (
              <option key={item.attemptId} value={item.attemptId}>
                {workOption(item)}
              </option>
            ))}
          </select>
        </>
      )}
      {failure && (
        <p className={ui.error} role="alert">
          {FAILURE_COPY[failure]}
        </p>
      )}
      <div className={ui.sheetActions}>
        <button
          type="button"
          className={`${ui.button} ${ui.secondary}`}
          onClick={onCancel}
          disabled={saving}
        >
          Cancel
        </button>
        <button
          type="submit"
          className={`${ui.button} ${ui.primary}`}
          disabled={saving || !text.trim()}
        >
          {saving ? "Saving…" : "Save"}
        </button>
      </div>
    </form>
  );
};

// A teacher's notes about one learner. Not a review, score or category:
// just what they noticed, when, and optionally which of their recent work it
// was about. `work` is [{ attemptId, deliveryId, title, when }], newest first.
const Observations = ({ classroomId, learnerId, observations, work = [], describe }) => {
  const [adding, setAdding] = useState(false);
  const [added, setAdded] = useState([]);
  const [justSaved, setJustSaved] = useState(false);

  // Notes saved here show at once, before the next refresh brings them back.
  const known = new Set(observations.map((item) => item.id));
  const shown = [...added.filter((item) => !known.has(item.id)), ...observations];

  const onSaved = (saved) => {
    setAdded((list) => [saved, ...list.filter((item) => item.id !== saved.id)]);
    setAdding(false);
    setJustSaved(true);
    announceWorkspaceChange();
  };

  return (
    <section className={ui.stack} aria-labelledby="observations">
      <div className={ui.row}>
        <h2 id="observations" className={ui.cardTitle}>
          Observations
        </h2>
        {!adding && (
          <button
            type="button"
            className={`${ui.button} ${ui.secondary}`}
            onClick={() => {
              setAdding(true);
              setJustSaved(false);
            }}
          >
            Add observation
          </button>
        )}
      </div>
      {justSaved && (
        <p className={ui.meta} role="status">
          Observation saved.
        </p>
      )}
      {adding && (
        <AddObservation
          classroomId={classroomId}
          learnerId={learnerId}
          work={work}
          onSaved={onSaved}
          onCancel={() => setAdding(false)}
        />
      )}
      {shown.length ? (
        <ul className={ui.list}>
          {shown.map((item) => {
            const about = describe?.(item);
            return (
              <li key={item.id} className={`${ui.card} ${ui.compactCard}`}>
                <p className={ui.preserveLines}>{item.observation}</p>
                <p className={ui.meta}>
                  {[formatWhen(item.observedAt), about && `About ${about}`]
                    .filter(Boolean)
                    .join(" · ")}
                </p>
              </li>
            );
          })}
        </ul>
      ) : (
        !adding && <p className={ui.meta}>No observations yet.</p>
      )}
    </section>
  );
};

export default Observations;
